import type { Currency } from '@ps/web-core-enums';
import { Translation } from '@ps/web-shared-components';
import React from 'react';

import CurrencyAmount from '@swf/components/CurrencyAmount/CurrencyAmount';
import type { MultiPurposeNotification } from '@swf/core/types/Notification';

import type { CardAttributes } from '../../config/NotificationTypes';
import { FormatType } from '../../enums';
import { formatDate, getConfiguredAttributes, geti18NValueConfig } from '../../utils';

import styles from './Attributes.scss';

export interface Props {
  displayAttributes?: CardAttributes[];

  attributes: MultiPurposeNotification['attributes'];
  locale: string;
}
/**
 *
 * @param attributes - Full list of attributes from server response - eg. num of plays, expiry date, etc
 * @param displayAttributes - List of attributes and configuration for display on view
 *
 * @returns List of {key: value} objects where each key is translated, each value is formatted according to the displayAttribute rule
 */
/* eslint-disable @typescript-eslint/no-explicit-any */
export type FormatAttributes = (attrs: {
  attributes: MultiPurposeNotification['attributes'];
  displayAttributes: CardAttributes[];
  locale: string;
}) => Record<string, any>[];
/* eslint-enable @typescript-eslint/no-explicit-any */

const FormattedValue: React.FC<{
  value: string;
  formatterType: FormatType;
  formatterValue: string;
  locale: string;
}> = ({ value, formatterType, formatterValue, locale }) => {
  switch (formatterType) {
    case FormatType.Date:
      return <span>{formatDate(value, locale)}</span>;
    case FormatType.Number:
      return <span>{new Intl.NumberFormat(locale, { style: 'decimal' }).format(+value)}</span>;
    case FormatType.Currency:
      return (
        <CurrencyAmount
          value={parseInt(value)}
          currency={formatterValue as Currency}
          minimumFractionDigits={Number.isInteger(parseInt(value) / 100) ? 0 : 2}
        />
      );
    default:
      return <span>{value}</span>;
  }
};

const formattedAttrList: FormatAttributes = ({ attributes, displayAttributes, locale }) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getConfiguredAttributes(attributes, displayAttributes).map((attr: Record<string, any>) => ({
    key: (
      <Translation
        {...attr.i18nTagCfg.tag}
        values={geti18NValueConfig(attr.i18nTagCfg.values, attributes)}
      />
    ),
    value: (
      <FormattedValue
        value={attr.value}
        formatterType={attr.formatterType}
        formatterValue={attr.formatterValue}
        locale={locale}
      />
    )
  }));

const Attributes: React.FC<Props> = ({ displayAttributes, attributes, locale }) => {
  if (!displayAttributes || displayAttributes.length === 0) {
    return null;
  }

  const attributeElements = formattedAttrList({
    attributes,
    displayAttributes,
    locale
  }).map((attr, idx) => {
    const {
      key: { props: { id = idx } = {} }
    } = attr;
    return (
      <div className={styles.notificationAttribute} key={id}>
        <span className={styles.attributeKey}>{attr.key} </span>
        <span className={styles.attributeValue}>{attr.value}</span>
      </div>
    );
  });
  return <section className={styles.notificationAttributeList}>{attributeElements}</section>;
};

export default Attributes;
